import React from 'react';
import { Container, Typography, Box } from '@mui/material';
import { Helmet } from 'react-helmet';
import { ArticleSchema, BreadcrumbSchema } from '../components/StructuredData';

const About = () => {
  return (
    <Container sx={{ padding: '0 20px' }}>
      <Helmet>
        <title>About - SalaryTools.co.uk</title>
        <meta name="description" content="Learn about SalaryTools.co.uk – your resource for understanding your finances, including salary breakdowns, tax, national insurance, and take-home pay." />
        <link rel="canonical" href="https://salarytools.co.uk/about" />
      </Helmet>
      <ArticleSchema 
        title="About SalaryTools.co.uk - UK Salary Calculator Tool"
        description="Learn about SalaryTools.co.uk – your resource for understanding your finances, including salary breakdowns, tax, national insurance, and take-home pay."
        datePublished="2024-01-01"
        dateModified={new Date().toISOString().split('T')[0]}
        url="https://salarytools.co.uk/about"
      />
      <BreadcrumbSchema 
        items={[
          { name: "Home", url: "https://salarytools.co.uk" },
          { name: "About", url: "https://salarytools.co.uk/about" }
        ]}
      />

      <Box sx={{ pt: 8, mb: 8 }}>
        <Box sx={{ marginBottom: '20px' }}>
          <Typography variant="h4" component="h1" gutterBottom>
            About This Site
          </Typography>
        <Typography variant="body1" paragraph>
          Welcome to SalaryTools.co.uk. This site is designed to help you manage and 
          understand your finances more easily by providing breakdowns of key 
          financial data such as salary, tax, national insurance, and take-home pay. Our goal is to 
          present your financial information in an accessible, easy-to-understand format.
        </Typography>
      </Box>

      <Box sx={{ marginBottom: '20px' }}>
        <Typography variant="h5" component="h2" gutterBottom>
          Key Features:
        </Typography>
        <ul>
          <li>
            <Typography variant="body1" paragraph>
              <strong>Annual vs Monthly Summary:</strong> View your salary, tax paid, and take-home pay 
              in both annual and monthly formats. This feature helps you compare your financial situation over time.
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              <strong>Effective Tax Rate Calculation:</strong> The app automatically calculates your effective 
              tax rate based on your salary and tax paid, giving you a clear picture of your overall tax burden. Note the Tax Rate
              includes National Insurance contributions.
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              <strong>National Insurance Breakdown:</strong> View how much of your salary is going toward 
              National Insurance contributions.
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              <strong>Visualizing Tax and National Insurance Changes:</strong> See how your tax and National Insurance 
              costs change as your salary increases or decreases. The app gives you a clear, visual representation 
              of how these deductions evolve based on your income.
            </Typography>
          </li>
        </ul>
      </Box>

      <Box sx={{ marginBottom: '20px' }}>
        <Typography variant="h5" component="h2" gutterBottom>
          Why Use This Site?
        </Typography>
        <Typography variant="body1" paragraph>
          This site provides a clear and simple way to understand your finances. Whether you’re 
          planning for your future or trying to get a better sense of your current financial health, 
          this tool can help you track important metrics. By visualizing your finances in an easy-to-digest 
          format, you can make more informed decisions about saving, spending, and budgeting.
        </Typography>
      </Box>

      <Box sx={{ marginBottom: '20px' }}>
        <Typography variant="h5" component="h2" gutterBottom>
          How Does It Work?
        </Typography>
        <Typography variant="body1" paragraph>
          The app takes key financial data (such as your salary, tax, and national insurance contributions) 
          and breaks it down into both annual and monthly views. This information is then presented in a 
          user-friendly grid, with clear labels and visual cues to help you understand how much you’re earning, 
          what you’re paying in taxes, and how much you’re taking home. 
        </Typography>
        <Typography variant="body1" paragraph>
          Additionally, as your salary changes, the app will show you how both your tax and national insurance 
          contributions change, allowing you to clearly see the impact of salary increases or decreases on these 
          important costs. This visualization can help you make more informed decisions when it comes to planning 
          your career or financial future.
        </Typography>
      </Box>
      </Box>
    </Container>
  );
};

export default About;
